import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";

export const ContactForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t("contact.form.errorMessage"));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="contact-form-success" className="bg-[#141414] border border-white/10 rounded-xl p-8 text-center">
        <CheckCircle2 className="text-gold mx-auto mb-4" size={40} />
        <h3 className="font-serif text-xl text-white mb-2">{t("contact.form.successTitle")}</h3>
        <p className="text-white/60 text-sm">{t("contact.form.successMessage")}</p>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={handleSubmit} className="bg-[#141414] border border-white/10 rounded-xl p-6 sm:p-8 space-y-5">
      <div>
        <h3 className="font-serif text-xl text-white mb-1">{t("contact.form.title")}</h3>
        <p className="text-white/50 text-sm">{t("contact.form.subtitle")}</p>
      </div>
      <div>
        <Label className="text-white/70 text-sm">{t("contact.form.name")}</Label>
        <Input data-testid="contact-form-name-input" required value={form.name} onChange={update("name")} placeholder={t("contact.form.namePlaceholder")} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" />
      </div>
      <div>
        <Label className="text-white/70 text-sm">{t("contact.form.email")}</Label>
        <Input data-testid="contact-form-email-input" type="email" required value={form.email} onChange={update("email")} placeholder={t("contact.form.emailPlaceholder")} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" />
      </div>
      <div>
        <Label className="text-white/70 text-sm">{t("contact.form.phone")}</Label>
        <Input data-testid="contact-form-phone-input" value={form.phone} onChange={update("phone")} placeholder={t("contact.form.phonePlaceholder")} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" />
      </div>
      <div>
        <Label className="text-white/70 text-sm">{t("contact.form.message")}</Label>
        <Textarea data-testid="contact-form-message-input" required rows={4} value={form.message} onChange={update("message")} placeholder={t("contact.form.messagePlaceholder")} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" />
      </div>
      {error && <p data-testid="contact-form-error" className="text-red-400 text-sm">{error}</p>}
      <Button type="submit" data-testid="contact-form-submit" disabled={sending} className="w-full bg-gold text-white hover:bg-gold-hover rounded-none disabled:opacity-50">
        {sending ? t("contact.form.sending") : t("contact.form.submit")}
      </Button>
    </form>
  );
};
